import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { API_URL, STATUS_STYLE, statusLabel, fmtUGX, downloadFeesExport, viewReceipt } from './feesUtils';

export const StudentStatementDialog = ({ studentId, year, term, onClose }) => {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!studentId) { setSt(null); return; }
    axios.get(`${API_URL}/api/fees/statement/${studentId}`, { params: { academic_year: year, term } })
      .then((r) => setSt(r.data)).catch(() => {});
  }, [studentId, year, term]);

  return (
    <Dialog open={!!studentId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="student-statement-dialog">
        {st && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                {st.student.name}
                <Badge className={`capitalize ${STATUS_STYLE[st.summary.payment_status] || ''}`} variant="outline">
                  {statusLabel(st.summary.payment_status)}
                </Badge>
              </DialogTitle>
              <DialogDescription>
                {st.student.student_class} · {year} Term {term}
                {st.student.registration_number ? ` · ${st.student.registration_number}` : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2 text-sm" data-testid="statement-summary">
              {[['Expected Fees', fmtUGX(st.summary.expected_fees)], ['Total Paid', fmtUGX(st.summary.total_paid)],
                ['Current Balance', fmtUGX(st.summary.current_balance)], ['Previous Arrears', fmtUGX(st.summary.previous_arrears)],
                ['Arrears Paid', fmtUGX(st.summary.arrears_paid)], ['Total Outstanding', fmtUGX(st.summary.total_outstanding)]].map(([l, v]) => (
                  <div key={l} className="rounded-md bg-muted/50 p-2">
                    <p className="text-[11px] text-muted-foreground">{l}</p><p className="font-semibold">{v}</p>
                  </div>
                ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadFeesExport('statement',
                { academic_year: year, term, student_id: st.student.id, format: 'pdf' },
                `statement_${st.student.name}.pdf`)} data-testid="statement-export-pdf">
                <FileText className="h-3.5 w-3.5 mr-1" /> PDF Statement
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadFeesExport('statement',
                { academic_year: year, term, student_id: st.student.id, format: 'excel' },
                `statement_${st.student.name}.xlsx`)} data-testid="statement-export-excel">
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel
              </Button>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Category</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Method</TableHead>
                <TableHead>Receipt No.</TableHead><TableHead>Receipt</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {st.payments.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No payments this term</TableCell></TableRow>
                ) : st.payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-xs">{p.date}</TableCell>
                    <TableCell className="capitalize">{p.category.replace(/_/g, ' ')}</TableCell>
                    <TableCell className="text-right font-medium">{fmtUGX(p.amount)}</TableCell>
                    <TableCell className="capitalize text-xs">{(p.payment_method || '').replace(/_/g, ' ')}</TableCell>
                    <TableCell className="text-xs">{p.receipt_number || '—'}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => viewReceipt(p.id)} data-testid={`stmt-receipt-${p.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const StatementsTab = ({ config, school, year, term, refreshKey }) => {
  const [classSel, setClassSel] = useState('');
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const classes = config?.schools?.find((s) => s.key === school)?.classes || [];

  useEffect(() => { setClassSel(''); setStudents([]); }, [school]);
  useEffect(() => {
    if (!classSel) return;
    axios.get(`${API_URL}/api/fees/tracker`, { params: { school, academic_year: year, term, class_name: classSel } })
      .then((r) => setStudents(r.data.rows)).catch(() => {});
  }, [classSel, school, year, term, refreshKey]);

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Student Payment Statements</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-3">
          <Select value={classSel} onValueChange={setClassSel}>
            <SelectTrigger className="w-52" data-testid="stmt-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Student</TableHead><TableHead className="text-right">Total Paid</TableHead>
            <TableHead className="text-right">Outstanding</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {students.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                {classSel ? 'No students in this class' : 'Pick a class to list students'}
              </TableCell></TableRow>
            ) : students.map((s) => (
              <TableRow key={s.student_id}>
                <TableCell className="font-medium">{s.student_name}</TableCell>
                <TableCell className="text-right text-green-600">{fmtUGX(s.total_paid)}</TableCell>
                <TableCell className="text-right text-red-600">{fmtUGX(s.total_outstanding)}</TableCell>
                <TableCell><Badge className={`capitalize ${STATUS_STYLE[s.payment_status] || ''}`} variant="outline">{statusLabel(s.payment_status)}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => setSelected(s.student_id)} data-testid={`open-statement-${s.student_id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> Statement
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <StudentStatementDialog studentId={selected} year={year} term={term} onClose={() => setSelected(null)} />
      </CardContent>
    </Card>
  );
};
